/**
 * Client 360's **Cases** tab — the cases and the due dates, the two seams
 * Wave 2A left and the Wave 3 close assigned to 4A (§6 item 5). Two section
 * cards, each a kit table since Wave 15B (C14 §10.6 items 2–3; T-6.14).
 *
 * One read serves both (`GET /api/v1/cases/client/{id}`), because they are two
 * halves of one question — *"what is the firm doing for this client, and what
 * is coming"* — and they always render together.
 *
 * **The Due-dates part can be legitimately absent.** Spec §P gives an
 * associate, a reviewer and an article assistant no verb on `calendar` at all,
 * so the server answers `due_dates: null` for them rather than refusing the
 * whole read and taking the Cases part with it. `null` is not `[]`: an empty
 * list renders as *"this client has nothing due"*, which is a different and
 * false statement, so the part says what is actually true — that this person
 * cannot see the compliance calendar.
 */

import { Alert, Skeleton, TONE_TEXT } from '@fiveox/ui'
import { Briefcase, CalendarClock } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'

import {
  type ListColumn,
  ListTable,
  RecordSection,
  StatusBadge,
  useListTable,
} from '../../components/page'
import {
  type Case,
  type ClientPanels as Panels,
  CATEGORY_LABELS,
  type StageCategory,
  caseFlows,
  caseLabel,
  dueLabel,
} from '../../lib/api/cases'

type DueRow = NonNullable<Panels['due_dates']>[number]

/** Stable empties, so a list's memo does not see a new array every render. */
const NO_CASES: Case[] = []
const NO_DUE: DueRow[] = []

/** How a due date reads: late in the error tone, today in ink, the rest muted. */
export function DueCell({ due }: { due: string | null }) {
  const label = dueLabel(due)
  const tone =
    label.tone === 'overdue'
      ? `${TONE_TEXT.error} font-medium`
      : label.tone === 'today'
        ? 'text-foreground font-medium'
        : label.tone === 'soon'
          ? 'text-foreground'
          : 'text-muted-foreground'
  return <span className={`whitespace-nowrap ${tone}`}>{label.text}</span>
}

function PartSkeleton() {
  return (
    <div role="status" aria-busy aria-label="Loading" className="space-y-2">
      <Skeleton className="h-9 w-full rounded-lg" />
      <Skeleton className="h-9 w-full rounded-lg" />
      <Skeleton className="h-9 w-2/3 rounded-lg" />
    </div>
  )
}

export function ClientCasePanels({ clientId }: { clientId: string }) {
  const navigate = useNavigate()
  const [panels, setPanels] = useState<Panels | null>(null)
  const [openCases, setOpenCases] = useState(0)
  const [byCategory, setByCategory] = useState<Record<string, number>>({})
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void caseFlows
      .clientPanels(clientId)
      .then((body) => {
        setPanels(body.data)
        setOpenCases(Number(body.meta?.open_cases ?? 0))
        setByCategory((body.meta?.by_category as Record<string, number>) ?? {})
      })
      .catch(() => setError('This client’s cases could not be loaded.'))
  }, [clientId])

  const caseColumns = useMemo<ListColumn<Case>[]>(
    () => [
      {
        key: 'title',
        label: 'Case',
        width: 220,
        required: true,
        phone: true,
        render: (row) => <span className="block max-w-[8rem] truncate sm:max-w-xs">{caseLabel(row)}</span>,
      },
      {
        key: 'number',
        label: 'Number',
        width: 130,
        sortField: 'number',
        render: (row) => <span className="text-muted-foreground font-mono text-xs">{row.number}</span>,
      },
      {
        key: 'status',
        label: 'Status',
        width: 150,
        phone: true,
        sortField: 'category',
        // The tone is the stage's category; the words are the firm's own
        // stage name (status.ts's rule for a case).
        render: (row) => <StatusBadge kind="case" status={row.category} label={row.status} />,
      },
      {
        key: 'due_date',
        label: 'Due',
        width: 110,
        sortField: 'due_date',
        render: (row) => <DueCell due={row.due_date} />,
      },
    ],
    [],
  )

  const dueColumns = useMemo<ListColumn<DueRow>[]>(
    () => [
      {
        key: 'item_name',
        label: 'What is due',
        width: 220,
        required: true,
        phone: true,
        sortField: 'item_name',
        render: (row) =>
          row.case_id ? (
            <Link
              to={`/cases/${row.case_id}`}
              onClick={(event) => event.stopPropagation()}
              className="text-foreground hover:text-primary font-medium"
            >
              {row.item_name}
            </Link>
          ) : (
            <span className="text-foreground">{row.item_name}</span>
          ),
      },
      {
        key: 'period',
        label: 'Period',
        width: 110,
        sortField: 'period',
        render: (row) => <span className="text-muted-foreground">{row.period}</span>,
      },
      {
        key: 'state',
        label: 'Case',
        width: 190,
        // A claimed period with no case is the state the reconciler exists
        // for. Named, because "pending" on its own reads as an error.
        render: (row) => (
          <span className="text-muted-foreground">
            {row.status === 'pending'
              ? 'Awaiting a case'
              : row.status === 'failed'
                ? 'The case could not be opened'
                : row.case_id
                  ? 'Opened'
                  : '—'}
          </span>
        ),
      },
      {
        key: 'due_date',
        label: 'Due',
        width: 110,
        phone: true,
        sortField: 'due_date',
        render: (row) => <DueCell due={row.due_date} />,
      },
    ],
    [],
  )

  const cases = panels?.cases ?? NO_CASES
  const dueDates = panels?.due_dates
  // The server's order until the reader sorts, as before the redraw.
  const caseList = useListTable(cases, { columns: caseColumns })
  const dueList = useListTable(dueDates ?? NO_DUE, { columns: dueColumns })

  if (error) {
    return (
      <RecordSection id="client-cases" icon={<Briefcase size={18} aria-hidden />} title="Cases">
        <Alert tone="error">{error}</Alert>
      </RecordSection>
    )
  }

  const summary =
    panels && cases.length > 0
      ? [
          `${openCases} open`,
          ...(Object.entries(byCategory) as Array<[StageCategory, number]>).map(
            ([category, count]) => `${CATEGORY_LABELS[category] ?? category}: ${count}`,
          ),
        ].join(' · ')
      : 'What the firm is doing for this client.'

  return (
    <div className="min-w-0 space-y-6">
      <RecordSection
        id="client-cases"
        icon={<Briefcase size={18} aria-hidden />}
        title="Cases"
        description={summary}
      >
        {!panels ? (
          <PartSkeleton />
        ) : (
          <ListTable
            list={caseList}
            loading={false}
            rowKey={(row) => row.id}
            rowHref={(row) => `/cases/${row.id}`}
            empty="Nothing open for this client yet."
          />
        )}
      </RecordSection>

      <RecordSection
        id="client-due-dates"
        icon={<CalendarClock size={18} aria-hidden />}
        title="Due dates"
        description="What the compliance calendar has generated for this client."
      >
        {!panels ? (
          <PartSkeleton />
        ) : dueDates === null || dueDates === undefined ? (
          <p className="text-muted-foreground text-sm">
            {/* Not an empty list, and not an error: this person's role holds no
                verb on the compliance calendar (spec §P). */}
            Your role does not include the compliance calendar, so what is due for this client is
            not shown here.
          </p>
        ) : (
          <ListTable
            list={dueList}
            loading={false}
            rowKey={(row) => row.id}
            onRowClick={(row) => {
              if (row.case_id) navigate(`/cases/${row.case_id}`)
            }}
            empty="Nothing generated for this client yet. The compliance calendar creates these from the items your firm has set up."
          />
        )}
      </RecordSection>
    </div>
  )
}
